// Turning a task's parameter form into the parameters it runs with, and editing a task's schedule list.

// A started task takes its company from the caller's token; a schedule fires without one, so it carries it.
export const COMPANY_PARAMETER = 'company_id';

const isBoolean = (param) => param.type === 'boolean' || param.type === 'bool';

// The form's starting values: each parameter's default, false for booleans, an empty list or JSON text.
export function defaultParameterValues(params) {
  const values = {};
  (params || []).forEach((param) => {
    if (param.default !== undefined && param.default !== null) {
      values[param.name] = param.default;
    } else if (isBoolean(param)) {
      values[param.name] = false;
    } else if (param.type === 'array') {
      values[param.name] = [];
    } else if (param.type === 'object') {
      values[param.name] = '';
    }
  });
  return values;
}

// The form values for a stored schedule config: the defaults, overlaid with the config, object parameters as JSON.
export function parameterValuesFromConfig(params, config) {
  const values = { ...defaultParameterValues(params), ...(config || {}) };
  (params || []).forEach((param) => {
    const value = values[param.name];
    if (param.type === 'object' && value !== null && typeof value === 'object') {
      values[param.name] = JSON.stringify(value, null, 2);
    }
  });
  return values;
}

// EventBridge names the schedule dt_<task_id>_<name>: at most 64 characters of [0-9a-zA-Z-_.].
const SCHEDULE_NAME_MAX = 64;
const SCHEDULE_NAME_CHARACTERS = /^[0-9a-zA-Z_.-]+$/;

export function scheduleNameError(taskId, name) {
  if (!name) return 'A name is required';
  if (!SCHEDULE_NAME_CHARACTERS.test(name)) return 'Use letters, digits, "-", "_" and "." only';
  const room = SCHEDULE_NAME_MAX - `dt_${taskId}_`.length;
  if (name.length > room) return `At most ${room} characters for this task`;
  return null;
}

// The parameters to send: empty values left out, booleans always sent, object fields parsed from their JSON text
// (kept as typed when it doesn't parse, so the API reports it).
export function buildTaskParameters(params, values, { includeCompany = false } = {}) {
  const result = {};
  (params || []).forEach((param) => {
    if (param.name === COMPANY_PARAMETER && !includeCompany) return;
    const value = values[param.name];
    if (value !== undefined && value !== null && value !== '') {
      if (param.type === 'object' && typeof value === 'string') {
        try {
          result[param.name] = JSON.parse(value);
        } catch (e) {
          result[param.name] = value;
        }
      } else {
        result[param.name] = value;
      }
    } else if (isBoolean(param)) {
      result[param.name] = value === true;
    }
  });
  return result;
}

// A schedule's config after editing: the declared parameters from the form, over the stored config. Stored keys the
// task doesn't declare are kept, and with no known parameters (task not loaded, or gone from the code) the stored
// config is kept whole: a save never drops configuration it can't show.
export function scheduleConfig(params, values, storedConfig) {
  const stored = storedConfig || {};
  if (!params || params.length === 0) return { ...stored };
  const declared = new Set(params.map((param) => param.name));
  const undeclared = Object.fromEntries(Object.entries(stored).filter(([key]) => !declared.has(key)));
  return { ...undeclared, ...buildTaskParameters(params, values, { includeCompany: true }) };
}

// The list with `schedule` added, or replacing the one named `originalName` when editing. Names are unique per task.
export function withSchedule(schedules, schedule, originalName = null) {
  const others = schedules.filter((s) => s.name !== originalName);
  if (others.some((s) => s.name === schedule.name)) {
    throw new Error(`A schedule named "${schedule.name}" already exists`);
  }
  if (originalName === null) return [...schedules, schedule];
  return schedules.map((s) => (s.name === originalName ? schedule : s));
}

export function withoutSchedule(schedules, name) {
  return schedules.filter((s) => s.name !== name);
}

export function withScheduleEnabled(schedules, name, enabled) {
  return schedules.map((s) => (s.name === name ? { ...s, enabled } : s));
}

// What the API takes: next_executions is computed by the API on each read.
export function schedulesPayload(schedules) {
  return schedules.map(({ name, expression, config, enabled }) => ({
    name,
    expression,
    config: config || {},
    enabled,
  }));
}
